import { action } from 'mobx';
import { uniqBy } from 'lodash';
import { Collection } from 'core/model';
import api from 'core/util/api';
import $auth from 'app/stores/$auth';
import { showSuccessToast, showErrorToast } from 'core/components';
import CustomNetworkContextModel from './CustomNetworkContextModel';

/**
 * Version history for the Custom Network Context (CNC).
 *
 * Revisions are append-only and share no row id, so (like RunbookRevisionCollection)
 * we deduplicate by `version` rather than the default `model.id`.
 *
 * `saveContext` writes a new revision and adds it to the collection directly from
 * the write response — it never refetches the list or the new revision's content.
 * This is what avoids the read-after-write staleness that a replica read would
 * otherwise introduce right after a save.
 */
class CustomNetworkContextCollection extends Collection {
  get url() {
    return '/api/ui/ai/context/versions';
  }

  get model() {
    return CustomNetworkContextModel;
  }

  get defaultSortState() {
    return { field: 'version', direction: 'desc' };
  }

  // Dedupe by version (revisions have no distinct row id on the client).
  @action
  add = (data) => {
    const rows = Array.isArray(data) ? data : [data];

    const models = rows.map((row) => {
      if (row instanceof CustomNetworkContextModel) {
        row.collection = this;
        return row;
      }
      return this.build(row);
    });

    this.original = uniqBy(this.get().concat(models), (model) => String(model.version));
    this.filter();
    this.hashModelsById();
    this.setLastUpdated();

    return models;
  };

  // Models are kept sorted descending by version, so the next entry is the prior one.
  getPreviousVersion(model) {
    const index = this.models.indexOf(model);
    return index >= 0 && index < this.models.length - 1 ? this.models[index + 1] : null;
  }

  // Resolves 'latest' or an explicit version to its model, or null if not found/empty.
  getVersionModel(versionId) {
    return (
      this.models.find((model, index) =>
        versionId === 'latest' ? index === 0 : String(model.version) === String(versionId)
      ) || null
    );
  }

  // True while the version list, or the resolved model's own content, is still loading.
  isLoadingVersion(model) {
    return (
      !this.hasFetched ||
      this.isRequestActive('fetching') ||
      (!!model && (model.isRequestActive('fetching') || (!model.hasContent && !model.error)))
    );
  }

  /**
   * Persist a new revision and reflect it locally from the response.
   *
   * @param {Object} params
   * @param {string} params.context - the prompt body being saved
   * @param {string} [params.comment] - optional author comment
   * @param {boolean} [params.showToast] - set false when the caller shows its own success toast
   * @returns {Promise<CustomNetworkContextModel>} the newly added model
   */
  @action
  saveContext = ({ context, comment, showToast = true }) => {
    this.requestStatus = 'updating';
    return api
      .put('/api/ui/ai/context', { data: { context, comment } })
      .then(
        action((response) => {
          if (!response || !response.success) {
            throw new Error('Server indicated failure.');
          }

          const [model] = this.add({
            version: response.version,
            context,
            comment: comment || null,
            cdate: response.cdate,
            user: { user_full_name: $auth.getActiveUserProperty('user_full_name') }
          });

          this.requestStatus = null;
          if (showToast) {
            showSuccessToast('Custom Network Context updated successfully.');
          }
          return model;
        })
      )
      .catch(
        action((err) => {
          this.requestStatus = null;
          showErrorToast('Failed to save Custom Network Context.');
          throw err;
        })
      );
  };
}

export default CustomNetworkContextCollection;
